import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ChevronLeft, Camera, Trash2, Mail, Lock, Check,
  AlertCircle, ShieldCheck, ChevronRight, X,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";

// Edit Account screen — reached by tapping the hero card on /profile.
// Handles: display name, avatar (add/change/remove), email view,
// change email (with password + verification), change password,
// email verification status + resend.
//
// This is strictly ACCOUNT INFO. "Build My Profile" (basics, goals,
// preferences) remains a separate screen at /profile-setup.

function Section({ title, children, testId }) {
  return (
    <section
      data-testid={testId}
      style={{
        background: "var(--bg-elev-1, #141414)",
        border: "1px solid var(--line, #1f1f1f)",
        borderRadius: 14,
        padding: "18px 18px 20px",
        marginBottom: 14,
      }}
    >
      <div
        className="eyebrow"
        style={{ marginBottom: 12, fontSize: 11, letterSpacing: ".22em" }}
      >
        {title}
      </div>
      {children}
    </section>
  );
}

function InlineMessage({ tone = "info", children, testId }) {
  if (!children) return null;
  const bg =
    tone === "error"   ? "rgba(255,120,120,0.10)" :
    tone === "success" ? "rgba(184,255,208,0.10)" :
                         "rgba(232,211,185,0.10)";
  const color =
    tone === "error"   ? "#ffb0b0" :
    tone === "success" ? "var(--good, #a8e0be)" :
                         "var(--text)";
  const Icon = tone === "error" ? AlertCircle : Check;
  return (
    <div
      data-testid={testId}
      style={{
        display: "flex", alignItems: "center", gap: 8,
        background: bg, color, padding: "8px 12px",
        borderRadius: 10, fontSize: 13, marginTop: 10,
      }}
    >
      <Icon size={14} strokeWidth={1.9} />
      <span>{children}</span>
    </div>
  );
}

export default function EditAccountScreen() {
  const navigate = useNavigate();
  const { user, refresh } = useAuth();
  const fileInputRef = useRef(null);

  // ---- Local editable state
  const [name, setName] = useState("");
  const [avatarPreview, setAvatarPreview] = useState(null); // data-URL (base64)
  const [avatarChanged, setAvatarChanged] = useState(false);
  const [avatarRemoved, setAvatarRemoved] = useState(false);

  // ---- Save profile
  const [savingProfile, setSavingProfile] = useState(false);
  const [profileMsg, setProfileMsg] = useState({ tone: null, text: "" });

  // ---- Change password
  const [pwCurrent, setPwCurrent] = useState("");
  const [pwNew, setPwNew] = useState("");
  const [pwConfirm, setPwConfirm] = useState("");
  const [savingPw, setSavingPw] = useState(false);
  const [pwMsg, setPwMsg] = useState({ tone: null, text: "" });

  // ---- Change email
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailPw, setEmailPw] = useState("");
  const [emailNew, setEmailNew] = useState("");
  const [savingEmail, setSavingEmail] = useState(false);
  const [emailMsg, setEmailMsg] = useState({ tone: null, text: "" });

  // ---- Verify email
  const [resendingVerify, setResendingVerify] = useState(false);
  const [verifyMsg, setVerifyMsg] = useState({ tone: null, text: "" });

  useEffect(() => {
    if (user) {
      setName(user.name || "");
      setAvatarPreview(user.avatar ? `data:image/jpeg;base64,${user.avatar}` : null);
      setAvatarChanged(false);
      setAvatarRemoved(false);
    }
  }, [user]);

  if (!user) return null;
  const initial = (user?.name || user?.email || "G").trim()[0]?.toUpperCase() || "G";

  const onPickPhoto = () => fileInputRef.current?.click();

  const onPhotoChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-selecting the same file
    if (!file) return;
    if (file.size > 6 * 1024 * 1024) {
      setProfileMsg({ tone: "error", text: "Photo is too large (max 6 MB)." });
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setAvatarPreview(reader.result); // data-URL
      setAvatarChanged(true);
      setAvatarRemoved(false);
    };
    reader.readAsDataURL(file);
  };

  const onRemovePhoto = () => {
    setAvatarPreview(null);
    setAvatarRemoved(true);
    setAvatarChanged(false);
  };

  const nothingChanged =
    (name.trim() === (user.name || "")) && !avatarChanged && !avatarRemoved;

  const saveProfile = async () => {
    setProfileMsg({ tone: null, text: "" });
    if (!name.trim()) {
      setProfileMsg({ tone: "error", text: "Name can't be empty." });
      return;
    }
    const payload = {};
    if (name.trim() !== (user.name || "")) payload.name = name.trim();
    if (avatarChanged && avatarPreview) {
      // Strip the "data:image/..;base64," prefix — backend accepts either.
      const raw = avatarPreview.split(",").pop();
      payload.avatar_base64 = raw;
    } else if (avatarRemoved) {
      payload.avatar_base64 = "";
    }
    if (Object.keys(payload).length === 0) return;
    setSavingProfile(true);
    try {
      await api.patch("/account/profile", payload);
      await refresh?.();
      setProfileMsg({ tone: "success", text: "Saved." });
      setAvatarChanged(false);
      setAvatarRemoved(false);
    } catch (err) {
      setProfileMsg({
        tone: "error",
        text: err?.response?.data?.detail || "Couldn't save. Try again.",
      });
    } finally {
      setSavingProfile(false);
    }
  };

  const changePassword = async (e) => {
    e.preventDefault();
    setPwMsg({ tone: null, text: "" });
    if (pwNew.length < 6) {
      setPwMsg({ tone: "error", text: "Password must be at least 6 characters." });
      return;
    }
    if (pwNew !== pwConfirm) {
      setPwMsg({ tone: "error", text: "Passwords don't match." });
      return;
    }
    setSavingPw(true);
    try {
      await api.post("/account/change-password", {
        current_password: pwCurrent,
        new_password: pwNew,
      });
      setPwCurrent(""); setPwNew(""); setPwConfirm("");
      setPwMsg({ tone: "success", text: "Password updated." });
    } catch (err) {
      setPwMsg({
        tone: "error",
        text: err?.response?.data?.detail || "Couldn't update password.",
      });
    } finally {
      setSavingPw(false);
    }
  };

  const submitChangeEmail = async (e) => {
    e.preventDefault();
    setEmailMsg({ tone: null, text: "" });
    if (!emailNew.trim()) return;
    setSavingEmail(true);
    try {
      const { data } = await api.post("/account/change-email/request", {
        current_password: emailPw,
        new_email: emailNew.trim().toLowerCase(),
      });
      setEmailMsg({ tone: "success", text: data?.message || "Verification link sent." });
      setEmailPw(""); setEmailNew("");
    } catch (err) {
      setEmailMsg({
        tone: "error",
        text: err?.response?.data?.detail || "Couldn't send verification link.",
      });
    } finally {
      setSavingEmail(false);
    }
  };

  const resendVerify = async () => {
    setVerifyMsg({ tone: null, text: "" });
    setResendingVerify(true);
    try {
      await api.post("/auth/verify-email/send");
      setVerifyMsg({ tone: "success", text: `Verification email sent to ${user.email}.` });
    } catch (err) {
      setVerifyMsg({
        tone: "error",
        text: err?.response?.data?.detail || "Couldn't send. Try again shortly.",
      });
    } finally {
      setResendingVerify(false);
    }
  };

  return (
    <div className="screen fade-in" data-testid="edit-account-screen">
      <div className="top-bar">
        <button
          type="button"
          className="icon-btn"
          onClick={() => navigate("/profile")}
          aria-label="Back"
          data-testid="edit-account-back-btn"
        >
          <ChevronLeft size={18} strokeWidth={1.7} />
        </button>
        <div className="logo-mark">ACCOUNT</div>
        <div style={{ width: 36 }} />
      </div>

      {/* ---------------- Profile (name + photo) ---------------- */}
      <Section title="PROFILE" testId="edit-account-profile-section">
        <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 18 }}>
          <div
            data-testid="edit-account-avatar-preview"
            style={{
              width: 72, height: 72, borderRadius: "50%",
              background: avatarPreview ? "#000" : "var(--gold-soft, rgba(232,211,185,0.14))",
              border: "1px solid var(--gold-line, rgba(232,211,185,0.34))",
              display: "flex", alignItems: "center", justifyContent: "center",
              color: "var(--gold, #e8d3b9)", fontFamily: "'Fraunces', serif", fontSize: 32,
              overflow: "hidden", flexShrink: 0,
            }}
          >
            {avatarPreview ? (
              <img
                src={avatarPreview}
                alt="Profile"
                style={{ width: "100%", height: "100%", objectFit: "cover" }}
              />
            ) : (
              initial
            )}
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            <button
              type="button"
              className="btn btn-outline"
              onClick={onPickPhoto}
              data-testid="edit-account-photo-pick-btn"
              style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 14px", fontSize: 13 }}
            >
              <Camera size={14} strokeWidth={1.9} />
              {avatarPreview ? "Change photo" : "Add photo"}
            </button>
            {avatarPreview && (
              <button
                type="button"
                onClick={onRemovePhoto}
                data-testid="edit-account-photo-remove-btn"
                style={{
                  background: "transparent", border: "none",
                  color: "var(--text-dim)", padding: 0, fontSize: 12,
                  display: "flex", alignItems: "center", gap: 6, cursor: "pointer",
                }}
              >
                <Trash2 size={12} strokeWidth={1.8} />
                Remove photo
              </button>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              hidden
              onChange={onPhotoChange}
              data-testid="edit-account-photo-input"
            />
          </div>
        </div>

        <div className="field" style={{ marginBottom: 14 }}>
          <label className="label" htmlFor="edit-name">Display name</label>
          <input
            id="edit-name"
            type="text"
            className="input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={80}
            data-testid="edit-account-name-input"
          />
        </div>

        <button
          type="button"
          className="btn btn-primary"
          onClick={saveProfile}
          disabled={savingProfile || nothingChanged}
          data-testid="edit-account-save-btn"
          style={{ width: "100%", justifyContent: "center" }}
        >
          {savingProfile ? "Saving…" : "Save changes"}
        </button>
        <InlineMessage tone={profileMsg.tone} testId="edit-account-profile-msg">
          {profileMsg.text}
        </InlineMessage>
      </Section>

      {/* ---------------- Email ---------------- */}
      <Section title="EMAIL" testId="edit-account-email-section">
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
          <Mail size={15} strokeWidth={1.8} color="var(--text-dim)" />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="body-sm" style={{ color: "var(--text)", wordBreak: "break-all" }} data-testid="edit-account-email-display">
              {user.email}
            </div>
            <div className="tiny" style={{ marginTop: 4, color: user.email_verified ? "var(--good, #a8e0be)" : "var(--text-faint)" }}>
              {user.email_verified ? (
                <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                  <ShieldCheck size={11} strokeWidth={2} /> Verified
                </span>
              ) : "Not verified"}
            </div>
          </div>
        </div>

        {!user.email_verified && (
          <>
            <button
              type="button"
              className="btn btn-outline"
              onClick={resendVerify}
              disabled={resendingVerify}
              data-testid="edit-account-resend-verify-btn"
              style={{ width: "100%", justifyContent: "center", fontSize: 13, padding: "10px 14px" }}
            >
              {resendingVerify ? "Sending…" : "Send verification email"}
            </button>
            <InlineMessage tone={verifyMsg.tone} testId="edit-account-verify-msg">
              {verifyMsg.text}
            </InlineMessage>
          </>
        )}

        <button
          type="button"
          onClick={() => { setEmailMsg({ tone: null, text: "" }); setShowEmailModal(true); }}
          data-testid="edit-account-change-email-btn"
          className="tile"
          style={{
            marginTop: 12, width: "100%",
            flexDirection: "row", alignItems: "center", textAlign: "left",
          }}
        >
          <div className="tile-icon"><Mail size={16} strokeWidth={1.7} /></div>
          <div style={{ flex: 1 }}>
            <div className="h3" style={{ color: "var(--text)" }}>Change email address</div>
            <div className="tiny" style={{ marginTop: 4, color: "var(--text-faint)" }}>
              We&apos;ll send a verification link to your new address before switching.
            </div>
          </div>
          <ChevronRight size={16} strokeWidth={1.5} color="var(--text-faint)" />
        </button>
      </Section>

      {/* ---------------- Password ---------------- */}
      <Section title="PASSWORD" testId="edit-account-password-section">
        <form onSubmit={changePassword} style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <div className="field">
            <label className="label" htmlFor="pw-current">Current password</label>
            <input
              id="pw-current"
              type="password"
              className="input"
              value={pwCurrent}
              onChange={(e) => setPwCurrent(e.target.value)}
              autoComplete="current-password"
              required
              data-testid="edit-account-current-password-input"
            />
          </div>
          <div className="field">
            <label className="label" htmlFor="pw-new">New password</label>
            <input
              id="pw-new"
              type="password"
              className="input"
              value={pwNew}
              onChange={(e) => setPwNew(e.target.value)}
              autoComplete="new-password"
              placeholder="At least 6 characters"
              required
              data-testid="edit-account-new-password-input"
            />
          </div>
          <div className="field">
            <label className="label" htmlFor="pw-confirm">Confirm new password</label>
            <input
              id="pw-confirm"
              type="password"
              className="input"
              value={pwConfirm}
              onChange={(e) => setPwConfirm(e.target.value)}
              autoComplete="new-password"
              required
              data-testid="edit-account-confirm-password-input"
            />
          </div>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={savingPw}
            data-testid="edit-account-change-password-btn"
            style={{ width: "100%", justifyContent: "center", display: "flex", alignItems: "center", gap: 8 }}
          >
            <Lock size={14} strokeWidth={1.9} />
            {savingPw ? "Updating…" : "Update password"}
          </button>
          <InlineMessage tone={pwMsg.tone} testId="edit-account-password-msg">
            {pwMsg.text}
          </InlineMessage>
        </form>
      </Section>

      {/* ---------------- Change-email modal ---------------- */}
      {showEmailModal && (
        <div
          data-testid="edit-account-change-email-modal"
          role="dialog"
          onClick={() => setShowEmailModal(false)}
          style={{
            position: "fixed", inset: 0, zIndex: 60,
            background: "rgba(0,0,0,0.72)", backdropFilter: "blur(4px)",
            display: "flex", alignItems: "flex-end", justifyContent: "center",
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              width: "100%", maxWidth: 520,
              background: "var(--bg-elev-1, #141414)",
              borderTopLeftRadius: 18, borderTopRightRadius: 18,
              border: "1px solid var(--line, #1f1f1f)",
              padding: "22px 22px 28px",
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
              <div className="eyebrow">CHANGE EMAIL</div>
              <button
                type="button"
                onClick={() => setShowEmailModal(false)}
                aria-label="Close"
                data-testid="edit-account-change-email-close"
                style={{ background: "transparent", border: "none", color: "var(--text-dim)", padding: 4 }}
              >
                <X size={18} strokeWidth={1.7} />
              </button>
            </div>
            <p className="body-sm" style={{ color: "var(--text-dim)", marginTop: 0, marginBottom: 16 }}>
              For your security, confirm your current password and enter the new address.
              We&apos;ll send a verification link there — your email only changes once you click it.
            </p>
            <form onSubmit={submitChangeEmail} style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              <div className="field">
                <label className="label" htmlFor="ce-pass">Current password</label>
                <input
                  id="ce-pass"
                  type="password"
                  className="input"
                  value={emailPw}
                  onChange={(e) => setEmailPw(e.target.value)}
                  autoComplete="current-password"
                  required
                  data-testid="edit-account-change-email-password-input"
                />
              </div>
              <div className="field">
                <label className="label" htmlFor="ce-new">New email</label>
                <input
                  id="ce-new"
                  type="email"
                  className="input"
                  value={emailNew}
                  onChange={(e) => setEmailNew(e.target.value)}
                  autoComplete="email"
                  placeholder="you@newdomain.com"
                  required
                  data-testid="edit-account-change-email-new-input"
                />
              </div>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={savingEmail}
                data-testid="edit-account-change-email-submit-btn"
                style={{ width: "100%", justifyContent: "center", marginTop: 4 }}
              >
                {savingEmail ? "Sending link…" : "Send verification link"}
              </button>
              <InlineMessage tone={emailMsg.tone} testId="edit-account-change-email-msg">
                {emailMsg.text}
              </InlineMessage>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
